import Alert from "components/Alert";
import { observer } from "mobx-react-lite";
import React from "react";
import { useTranslation } from "react-i18next";
import { IconBuildingSkyscraper, IconStack2 } from "@tabler/icons-react";
import { productUsageStore } from "features/ProductUsage/ProductUsageStore";
import ProductUsageSummaryCard from "./components/ProductUsageSummaryCard";
import ProductUsageTotals from "./components/ProductUsageTotals";
import ProductUsageList from "./components/ProductUsageList";

const buildingIcon = (
  <IconBuildingSkyscraper
    stroke={3}
    className="bg-gray-200 rounded-full p-1 w-6 h-6 text-gray-700"
  />
);

const componentIcon = (
  <IconStack2
    stroke={3}
    className="bg-cyan-300 rounded-full p-1 w-6 h-6 text-cyan-900"
  />
);

/**
 * Single warning shown in both the building product editor and the component
 * editor. It surfaces a buildings card and/or a components card depending on
 * which of them actually has affected data; when neither does, nothing renders.
 *
 * @param id Product or component id — both resolve through the same endpoints.
 */
const ProductUsageWarning = ({ id }: { id: string }) => {
  const { t } = useTranslation();

  React.useEffect(() => {
    // Clear the previous entity's summary first so we don't flash stale numbers
    // while the new one loads.
    productUsageStore.reset();
    if (!id) return;
    productUsageStore.loadSummary(id);
    return () => productUsageStore.reset();
  }, [id]);

  const summary = productUsageStore.summary;
  const { isUsedInBuildings, isUsedInComponents } = productUsageStore;

  if (!summary || !productUsageStore.isUsedAnywhere) return null;

  const title =
    isUsedInBuildings && isUsedInComponents
      ? t("productEditor.productUsageTitle", {
          buildingCount: summary.building_count,
          componentCount: summary.component_count,
        })
      : isUsedInBuildings
      ? t("productEditor.productUsageTitleBuildingsOnly", {
          buildingCount: summary.building_count,
        })
      : t("productEditor.productUsageTitleComponentsOnly", {
          componentCount: summary.component_count,
        });

  return (
    <>
      <Alert type="attention" className="mb-6" title={title}>
        <div className="mt-1 flex flex-col gap-3">
          {isUsedInBuildings && (
            <ProductUsageSummaryCard
              label={t("productEditor.productUsageBuildingsLabel")}
              icon={buildingIcon}
              metrics={[
                {
                  label: t("productEditor.productUsageBuildingsUnit"),
                  value: summary.building_count,
                },
                {
                  label: t("productEditor.productUsageMatches"),
                  value: summary.total_matches,
                },
                {
                  label: t("productEditor.productUsageCalculations"),
                  value: summary.total_calculations,
                },
              ]}
              onViewList={() => productUsageStore.openList("buildings")}
            />
          )}
          {isUsedInComponents && (
            <ProductUsageSummaryCard
              label={t("productEditor.productUsageComponentsLabel")}
              icon={componentIcon}
              metrics={[
                {
                  label: t("productEditor.productUsageDirectParents"),
                  value: summary.direct_parent_count,
                },
              ]}
              onViewList={() => productUsageStore.openList("components")}
            />
          )}
        </div>

        {isUsedInBuildings && (
          <ProductUsageTotals
            matches={summary.total_matches}
            calculations={summary.total_calculations}
          />
        )}
      </Alert>

      <ProductUsageList />
    </>
  );
};

export default observer(ProductUsageWarning);
